import { Trans } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import { useDefectName } from '@/models/defectType';
import { ArticleDefectWithRelations } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import {
  DefectBrandResponsibilityMobile,
  DefectBrandResponsibilityToggle,
  RemovesAllBrandResponsibilityWarning,
} from './BrandResponsibilityToggle';
import { DefectAddedByBadge } from './DefectAddedByBadge';
import { DefectContextInput, DefectContextMobile } from './DefectCardContext';
import { DefectCardHeader } from './DefectCardHeader';
import { DefectPhotosInput, DefectPhotosMobile } from './DefectCardPhotos';
import { DefectToBeRepairedBadge } from './DefectToBeRepairedBadge';
import {
  DefectExceptionalCoverageMobile,
  DefectExceptionalCoverageToggle,
  shouldShowExceptionalCoverageToggle,
} from './ExceptionalCoverageToggle';
import { ToBeRepairedToggle } from './ToBeRepairedToggle';

import './DefectsCard.css';

const { block } = createBEMClasses('defect-card');

export const DefectCard = ({
  defect,
  previousDefect,
  duplicateDefectNumber,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
  duplicateDefectNumber?: number;
}) => {
  const { isMobile } = useViewPort();
  const { article, view, errors } = useArticleContext();

  const defectOrPreviousDefect = defect ?? previousDefect!;
  const defectName = useDefectName(defectOrPreviousDefect);

  const exceptionalCoverageToggleShown = shouldShowExceptionalCoverageToggle({
    view,
    defect,
    article,
  });

  const hasError = !!errors?.defects?.defects?.find((def) => defect?.id === def.id)?.hasError;

  const shouldShowMobileAdditionalSection =
    (view.defects.fields.context.shown && !!defectOrPreviousDefect.context) ||
    (view.defects.fields.photo.shown && !!defectOrPreviousDefect.media.length) ||
    (view.defects.fields.toBeRepaired.shown && !!defectOrPreviousDefect.toBeRepaired) ||
    (view.defects.fields.brandResponsibility.enabled &&
      view.defects.fields.brandResponsibility.toggle.shown) ||
    (exceptionalCoverageToggleShown &&
      defectOrPreviousDefect.exceptionalBrandResponsibilityCoverageRequested) ||
    !!defectOrPreviousDefect.addedByWorkshopId ||
    !!defectOrPreviousDefect.addedByOrganizationId;

  const showBody = isMobile ? shouldShowMobileAdditionalSection : true;

  return (
    <Box
      padding="0"
      ariaLabel={duplicateDefectNumber ? `${defectName} #${duplicateDefectNumber}` : defectName}
      style={
        isMobile && hasError
          ? {
              border: '1px solid var(--color-danger-700)',
            }
          : undefined
      }
      className={block({
        new: !!defect && !!article.previousSnapshot?.articleDefects.length && !previousDefect,
        deleted: !defect && !!previousDefect,
      })}
    >
      {!defect && (
        <span className="visually-hidden">
          <Trans id="defect-card.deleted">Deleted defect</Trans>
        </span>
      )}

      <DefectCardHeader
        defect={defect}
        previousDefect={previousDefect}
        duplicateDefectNumber={duplicateDefectNumber}
      />

      {showBody && (
        <>
          <hr />
          {isMobile ? (
            <DefectCardMobile
              defect={defect}
              previousDefect={previousDefect}
              exceptionalCoverageToggleShown={exceptionalCoverageToggleShown}
            />
          ) : (
            <DefectCardDesktop
              defect={defect}
              previousDefect={previousDefect}
              exceptionalCoverageToggleShown={exceptionalCoverageToggleShown}
            />
          )}
        </>
      )}

      {view.defects.fields.brandResponsibility.enabled &&
        !view.defects.fields.brandResponsibility.toggle.shown &&
        defect?.removesAllBrandResponsibility && <RemovesAllBrandResponsibilityWarning />}
    </Box>
  );
};

const DefectCardMobile = ({
  defect,
  previousDefect,
  exceptionalCoverageToggleShown,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
  exceptionalCoverageToggleShown: boolean;
}) => {
  const { view } = useArticleContext();

  const defectOrPreviousDefect = defect ?? previousDefect!;

  return (
    <Stack padding="12px" gap="0.75rem">
      {view.defects.fields.context.shown && (
        <DefectContextMobile defect={defect} previousDefect={previousDefect} />
      )}
      {view.defects.fields.photo.shown && (
        <DefectPhotosMobile defect={defect} previousDefect={previousDefect} />
      )}
      <Stack gap="0.5rem" row>
        {view.defects.fields.toBeRepaired.shown && (
          <DefectToBeRepairedBadge defect={defect} previousDefect={previousDefect} />
        )}
        <DefectAddedByBadge defect={defect} previousDefect={previousDefect} />
        {view.defects.fields.brandResponsibility.enabled &&
          view.defects.fields.brandResponsibility.toggle.shown && (
            <DefectBrandResponsibilityMobile defect={defect} previousDefect={previousDefect} />
          )}
        {exceptionalCoverageToggleShown &&
          defectOrPreviousDefect.exceptionalBrandResponsibilityCoverageRequested && (
            <DefectExceptionalCoverageMobile defect={defect} previousDefect={previousDefect} />
          )}
      </Stack>
    </Stack>
  );
};

const DefectCardDesktop = ({
  defect,
  previousDefect,
  exceptionalCoverageToggleShown,
}: {
  defect?: ArticleDefectWithRelations;
  previousDefect?: ArticleDefectWithRelations;
  exceptionalCoverageToggleShown: boolean;
}) => {
  const { isTablet } = useViewPort();
  const { view } = useArticleContext();

  const showContextOrPhotos = view.defects.fields.context.shown || view.defects.fields.photo.shown;
  const showContextAndPhotos = view.defects.fields.context.shown && view.defects.fields.photo.shown;

  return (
    <Stack padding="12px" gap="1rem">
      {showContextOrPhotos && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isTablet || !showContextAndPhotos ? '1fr' : '1fr 1fr',
            gap: '1rem',
          }}
        >
          {view.defects.fields.photo.shown && (
            <DefectPhotosInput defect={defect} previousDefect={previousDefect} />
          )}
          {view.defects.fields.context.shown && (
            <DefectContextInput defect={defect} previousDefect={previousDefect} />
          )}
        </div>
      )}

      {view.defects.fields.brandResponsibility.enabled &&
        view.defects.fields.brandResponsibility.toggle.shown && (
          <DefectBrandResponsibilityToggle defect={defect} previousDefect={previousDefect} />
        )}

      {exceptionalCoverageToggleShown && (
        <DefectExceptionalCoverageToggle defect={defect} previousDefect={previousDefect} />
      )}

      {view.defects.fields.toBeRepaired.shown && (
        <ToBeRepairedToggle defect={defect} previousDefect={previousDefect} />
      )}
    </Stack>
  );
};
